import { Appointment, MedicalCertificate, SystemConfig } from '../types';
import { formatUkDate } from './dates';

const FALLBACK_LOGO_URL = 'https://placehold.co/150x50/009FE3/ffffff?text=K2+OH';
const FALLBACK_SIGNATURE_URL = 'https://placehold.co/150x50/white/000?text=Signed';

const escapeHtml = (value: unknown) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const isHttpLikeUrl = (value: string) => /^(https?:|data:image\/)/i.test(value);

const sanitizeImageUrl = (value: unknown, fallback: string) => {
  const raw = String(value ?? '').trim();
  if (!raw) return fallback;
  if (!isHttpLikeUrl(raw)) return fallback;
  return raw;
};

export const formatCertificateDate = (value?: string | null, fallback = 'N/A') => {
  const raw = String(value || '').trim();
  if (!raw) return fallback;
  return formatUkDate(raw, raw || fallback);
};

export const buildCertificateVerificationUrl = ({
  systemConfig,
  certificateId
}: {
  systemConfig: SystemConfig;
  certificateId: string;
}) => {
  const baseUrl = (
    systemConfig.verification?.urlOverride ||
    systemConfig.general.appUrl ||
    window.location.origin
  ).replace(/\/$/, '');

  return `${baseUrl}?mode=verify&certId=${encodeURIComponent(certificateId)}`;
};

const replaceImagePlaceholder = ({
  html,
  token,
  imageUrl,
  alt,
  style
}: {
  html: string;
  token: string;
  imageUrl: string;
  alt: string;
  style: string;
}) => {
  const escapedUrl = escapeHtml(imageUrl);
  const srcPlaceholderPattern = new RegExp(`src=(["'])\\{${token}\\}\\1`, 'g');
  const standalonePattern = new RegExp(`\\{${token}\\}`, 'g');

  return html
    .replace(srcPlaceholderPattern, `src="${escapedUrl}"`)
    .replace(
      standalonePattern,
      `<img src="${escapedUrl}" alt="${escapeHtml(alt)}" style="${escapeHtml(style)}" />`
    );
};

export const buildCertificateHtml = ({
  template,
  systemConfig,
  appointment,
  certificate,
  clientName
}: {
  template: string;
  systemConfig: SystemConfig;
  appointment: Appointment;
  certificate: MedicalCertificate;
  clientName: string;
}) => {
  const verificationUrl = buildCertificateVerificationUrl({
    systemConfig,
    certificateId: certificate.id
  });
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(verificationUrl)}`;

  const logoUrl = sanitizeImageUrl(systemConfig.general.logoUrl, FALLBACK_LOGO_URL);
  const signatureUrl = sanitizeImageUrl(certificate.technicianSignature, FALLBACK_SIGNATURE_URL);
  const jobTitle = String(appointment.customerJobTitle || '').trim();

  let html = String(template || '');
  html = replaceImagePlaceholder({
    html,
    token: 'app_logo',
    imageUrl: logoUrl,
    alt: 'App logo',
    style: 'max-height:56px;width:auto;object-fit:contain;'
  });
  html = replaceImagePlaceholder({
    html,
    token: 'signature',
    imageUrl: signatureUrl,
    alt: 'Technician signature',
    style: 'max-height:60px;width:auto;object-fit:contain;'
  });
  html = replaceImagePlaceholder({
    html,
    token: 'qr_code',
    imageUrl: qrCodeUrl,
    alt: 'Verification QR',
    style: 'width:100px;height:100px;'
  });

  const replacements: Record<string, string> = {
    name: escapeHtml(appointment.customerName || 'N/A'),
    dob: escapeHtml(formatCertificateDate(appointment.customerDob, 'N/A')),
    job_title: escapeHtml(jobTitle || 'N/A'),
    client_name: escapeHtml(clientName || 'N/A'),
    date_of_medical: escapeHtml(formatCertificateDate(certificate.issuedAt)),
    expiry_date: escapeHtml(formatCertificateDate(certificate.validUntil)),
    technician_name: escapeHtml(certificate.technicianName || 'N/A'),
    outcome: escapeHtml(
      String(certificate.outcome || 'N/A')
        .replace(/_/g, ' ')
        .toUpperCase()
    ),
    drug_outcome: escapeHtml(
      String(certificate.drugTestOutcome || 'N/A')
        .replace(/_/g, ' ')
        .toUpperCase()
    ),
    advice: escapeHtml(certificate.advice || 'None'),
    restrictions: certificate.restrictions
      ? `<p><strong>Restrictions:</strong> ${escapeHtml(certificate.restrictions)}</p>`
      : '',
    addons_list: escapeHtml((appointment.addOnIds || []).join(', ') || 'None'),
    ref: escapeHtml(certificate.id || '')
  };

  Object.entries(replacements).forEach(([token, replacement]) => {
    html = html.replace(new RegExp(`\\{${token}\\}`, 'g'), replacement);
  });

  return html;
};

const toFileSafeSegment = (value: string, fallback: string) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return normalized || fallback;
};

const formatFileDate = (value?: string | null) => {
  const raw = String(value || '').trim();
  if (!raw) return 'date-unknown';
  const prefixMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (prefixMatch) return `${prefixMatch[1]}-${prefixMatch[2]}-${prefixMatch[3]}`;
  return toFileSafeSegment(formatCertificateDate(raw, 'date-unknown'), 'date-unknown');
};

export const resolveCertificateClientName = (
  appointment: Appointment,
  companyNameFallback?: string
) => {
  const appointmentCompanyName = String(appointment.companyName || '').trim();
  if (appointmentCompanyName) return appointmentCompanyName;

  const fallback = String(companyNameFallback || '').trim();
  if (fallback) return fallback;

  if (appointment.companyId) return 'Corporate Client';
  return 'Individual';
};

export const buildCertificateFileName = ({
  appointment,
  certificate
}: {
  appointment: Appointment;
  certificate: MedicalCertificate;
}) => {
  const idPart = toFileSafeSegment(certificate.id, 'certificate');
  const namePart = toFileSafeSegment(appointment.customerName || 'patient', 'patient');
  const datePart = formatFileDate(certificate.issuedAt || appointment.date);
  return `certificate-${idPart}-${namePart}-${datePart}`;
};

export const openCertificatePrintWindow = (html: string, suggestedFileName?: string) => {
  if (!html) return false;
  const printWindow = window.open('', '', 'width=800,height=900');
  if (!printWindow) return false;

  printWindow.document.write(html);
  printWindow.document.close();
  const title = String(suggestedFileName || 'certificate').trim() || 'certificate';
  printWindow.document.title = title;
  setTimeout(() => printWindow.print(), 500);
  return true;
};
